import { Button, Tooltip, IconButton } from '@mui/material';
import T from 'i18n';
import {
  FiberManualRecord,
  KeyboardDoubleArrowLeft,
  ContentCopy,
  Description,
} from '@mui/icons-material';
import TypingEffect from 'features/common/TypingEffect';
import React, { createRef, useRef, useEffect, useMemo, useCallback } from 'react';
import history from 'common/history';
import { useDispatch, useSelector } from 'react-redux';
import { setCurrentSelectedFile } from '../redux/setCurrentSelectedFile';
import { setFileToCompare } from '../redux/setFileToCompare';
import { fetchFileDetails } from '../redux/fetchFileDetails';
import { setDocSearch } from 'features/viewing/redux/setDocSearch';
import { setDocSearchForCompare } from 'features/viewing/redux/setDocSearchForCompare';
import { selectCurrentCaseId, selectDocuments } from 'common/selectors';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { setSidebarOpen } from 'features/common/redux/navbarLayout';
import {
  DEFAULT_NAVIGATION_HIGHLIGHT_COLOR,
  COMPARE_NAVIGATION_HIGHLIGHT_COLOR,
} from 'features/viewing/AcusoftViewer/AcusoftViewerControl/constants';
import { useSetAISearchState } from '../redux/setAISearchState';

interface SearchAnswerContainerProps {
  response: any;
  typingEffect: boolean;
  scrollBarRef: any;
  handleMatchesLinkClicked?: any;
  allMatchesCount: number;
  allMatches?: any;
  isTranscriptPage?: boolean;
  fileSidebarAskAI?: boolean;
  rawAnswer?: string;
  gridView?: any;
  compare?: any;
  files?: any;
  directAnswers?: boolean;
}

const SearchAnswerContainer = ({
  response,
  rawAnswer,
  typingEffect,
  scrollBarRef,
  handleMatchesLinkClicked,
  allMatchesCount,
  allMatches,
  isTranscriptPage,
  fileSidebarAskAI,
  gridView,
  compare,
}: SearchAnswerContainerProps) => {
  const dispatch = useDispatch();
  const caseId = useSelector(selectCurrentCaseId);
  const documents = useSelector(selectDocuments) as Array<any>;
  const { clickedSource, setAIClickedSource } = useSetAISearchState();
  const [expandedSources, setExpandedSources] = React.useState<Set<string>>(new Set());
  // Local state to control tooltip text after successful copy actions
  const [copiedAnswer, setCopiedAnswer] = React.useState(false);
  const [copiedTable, setCopiedTable] = React.useState(false);
  const [hasTable, setHasTable] = React.useState(false);

  // Always call useRef unconditionally, then determine the content based on rawAnswer
  const textRefs = useRef<React.RefObject<HTMLDivElement>[]>(
    !rawAnswer ? response.map(() => createRef()) : [],
  );
  const answerContentRef = useRef<HTMLDivElement | null>(null);

  // Cache for storing fetchFileDetails results to prevent repetitive API calls
  const fileDetailsCache = useRef<{ [key: string]: any }>({});
  const fileToCompareCache = useRef<{ [key: string]: any }>({});

  // Common function to handle file loading with caching
  const loadFileWithCache = useCallback(
    async (
      fileId: string,
      onSuccess: (fileDetails: any) => void,
      cacheRef: React.MutableRefObject<{ [key: string]: any }> = fileDetailsCache,
    ) => {
      const cacheKey = `${caseId}-${fileId}`;
      const cachedFileDetails = cacheRef.current[cacheKey];

      if (cachedFileDetails) {
        // Use cached data instead of making API call
        onSuccess(cachedFileDetails);
        return cachedFileDetails;
      } else {
        // Check if fileId exists in the documents array
        const fileFromDocuments =
          documents && documents.length > 0 ? documents.find((doc) => doc.id === fileId) : null;

        if (fileFromDocuments) {
          // Use the file object from documents array
          cacheRef.current[cacheKey] = fileFromDocuments;
          onSuccess(fileFromDocuments);
          return fileFromDocuments;
        } else {
          // Make API call and cache the result
          try {
            const res = await dispatch(
              fetchFileDetails({ caseId, fileId, firstTrialBookOnly: true }),
            );
            // Preserve all properties from res, and add bundleLocations properties without overwriting
            const updateFileObjectWithLocation =
              res.bundleLocations && res.bundleLocations.length > 0
                ? { ...res.bundleLocations[0], ...res }
                : res;
            // Store the updated result in cache for future use
            cacheRef.current[cacheKey] = updateFileObjectWithLocation;
            onSuccess(updateFileObjectWithLocation);
            return updateFileObjectWithLocation;
          } catch (error) {
            console.error('Error fetching file details:', error);
          }
        }
      }
    },
    [caseId, documents, dispatch],
  );

  // Create table ID at the component level
  const tableId = useMemo(() => `table-${Math.random().toString(36).substr(2, 9)}`, []);

  // Create styles at the component level
  useEffect(() => {
    const styleId = 'markdown-table-styles';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        .markdown-table tbody tr:nth-child(even) {
          background-color: #E5E5E5 !important;
        }
        .markdown-table tbody tr:nth-child(odd) {
          background-color: transparent !important;
        }
        .table-container {
          position: relative;
          display: block;
          overflow-x: auto;
          max-width: 100%;
          -webkit-overflow-scrolling: touch;
          padding-bottom: 8px;
          padding-right: 24px; /* extra horizontal breathing room */
        }
        .table-copy-button {
          position: absolute;
          top: 0px;
          right: 0px;
          opacity: 0;
          transition: opacity 0.2s ease-in-out;
          background: rgba(255, 255, 255, 0.9);
          backdrop-filter: blur(4px);
          border: 1px solid #ddd;
          border-radius: 4px;
          z-index: 1;
        }
        .table-container:hover .table-copy-button {
          opacity: 1;
        }
        .table-copy-button:hover {
          background: rgba(255, 255, 255, 1);
        }
        .answer-container {
          position: relative;
          font-size: 14px !important;
          h1 { font-size:17px !important; }
          h2 { font-size:16px !important; }
          h3,h4,h5 { font-size:15px !important; }
        }
        .answer-copy-button {
          position: absolute;
          top: -3px;
          right: 0px;
          opacity: 0;
          transition: opacity 0.2s ease-in-out;
          background: rgba(255, 255, 255, 0.9);
          backdrop-filter: blur(4px);
          border: 1px solid #ddd;
          border-radius: 4px;
          z-index: 1;
        }
        .answer-container:hover .answer-copy-button {
          opacity: 1;
        }
        .answer-copy-button:hover {
          background: rgba(255, 255, 255, 1);
        }
        .answer-container.has-table .answer-copy-button {
          display: none;
        }
        /* Force tooltip colors with maximum specificity */
        .tooltip-first-file {
          background-color: #f2f7fe !important;
          color: #000000 !important;
        }
        .tooltip-first-file .MuiTooltip-arrow {
          color: #f2f7fe !important;
        }
        .tooltip-first-file .MuiTooltip-arrow::before {
          background-color: #f2f7fe !important;
        }
        .tooltip-second-file {
          background-color: #ebfef7 !important;
          color: #000000 !important;
        }
        .tooltip-second-file .MuiTooltip-arrow {
          color: #ebfef7 !important;
        }
        .tooltip-second-file .MuiTooltip-arrow::before {
          background-color: #ebfef7 !important;
        }
        .tooltip-default {
          background-color: #EBF3FE !important;
          color: #000000 !important;
        }
        .tooltip-default .MuiTooltip-arrow {
          color: #EBF3FE !important;
        }
        .tooltip-default .MuiTooltip-arrow::before {
          background-color: #EBF3FE !important;
        }
        .tooltip-title-card {
          background-color: #FFF !important;
          color: #000000 !important;
        }
        .tooltip-title-card .MuiTooltip-arrow {
          color: #F7F7F8 !important;
        }
        .tooltip-title-card .MuiTooltip-arrow::before {
          background-color: #F7F7F8 !important;
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  // Detect if current answer content contains a table to hide the answer-level copy button
  useEffect(() => {
    const checkForTable = () => {
      const container = answerContentRef.current;
      const found = !!(container && container.querySelector('table, .markdown-table'));
      setHasTable(found);
    };
    // initial check and next tick to wait for markdown render
    checkForTable();
    const id = setTimeout(checkForTable, 0);
    return () => clearTimeout(id);
  }, [rawAnswer, response, expandedSources]);

  // Limit the number of items to render to prevent excessive scrolling
  const MAX_ITEMS_TO_RENDER = 50;
  const itemsToRender = !rawAnswer ? response.slice(0, MAX_ITEMS_TO_RENDER) : [];
  const hasMoreItems = !rawAnswer ? response.length > MAX_ITEMS_TO_RENDER : [];

  const selectText = (index: number) => {
    const textElement = textRefs.current[index].current;
    if (textElement) {
      if (window.getSelection) {
        const selection = window.getSelection();
        const range = document.createRange();
        range.selectNodeContents(textElement);
        selection?.removeAllRanges();
        selection?.addRange(range);
      }
    }
  };

  const highlightText = (text: string, highlight: string) => {
    if (!highlight) return text;
    const regex = new RegExp(highlight, 'gi');
    const matches = text.match(regex);
    if (matches) {
      return text.replace(regex, `<span style="background-color: #ffff00">${matches[0]}</span>`);
    }
    return text;
  };

  // Function to copy table content to clipboard
  const copyTableToClipboard = async (tableElement: HTMLTableElement, rawMarkdown?: string) => {
    try {
      // Get the HTML content of the table
      const tableHTML = tableElement.outerHTML;

      // Get markdown format for fallback
      let markdownText = '';

      // If we have raw markdown text, try to extract the table portion
      if (rawMarkdown) {
        // Find table markdown in the raw text - more comprehensive regex
        const tableRegex = /\|[^\n]*\|[\s\S]*?(?=\n\n|\n(?!\|)|\n$|$)/g;
        const tableMatches = rawMarkdown.match(tableRegex);

        if (tableMatches && tableMatches.length > 0) {
          // Use the first table found and clean it up
          const tableText = tableMatches[0].trim();

          // Split into lines and process each line
          const lines = tableText.split('\n');
          const cleanedLines: string[] = [];

          lines.forEach((line, index) => {
            const trimmedLine = line.trim();
            if (trimmedLine.startsWith('|') && trimmedLine.endsWith('|')) {
              cleanedLines.push(trimmedLine);

              // Add separator row after the first row (header) if it doesn't exist
              if (index === 0 && lines.length > 1) {
                const nextLine = lines[index + 1]?.trim();
                if (!nextLine || !nextLine.match(/^\|[\s\-:]+\|$/)) {
                  // Count the number of columns to create separator
                  const columnCount = (trimmedLine.match(/\|/g) || []).length - 1;
                  const separator = '| ' + Array(columnCount).fill('---').join(' | ') + ' |';
                  cleanedLines.push(separator);
                }
              }
            }
          });

          markdownText = cleanedLines.join('\n');
        }
      }

      // Helpers to build URLs and inject hyperlinks into source groups
      const buildSourceUrl = (sourceNum: number): string | null => {
        const matchData = allMatches && allMatches[sourceNum - 1];
        if (!matchData) return null;
        const fileId = matchData.fileId || matchData.file?.id;
        const currentPageNumber = matchData.location?.pageNumber;
        if (!fileId || !currentPageNumber) return null;
        return `${window.location.origin}/case/${caseId}/files/${fileId}?goToPage=${currentPageNumber}`;
      };

      const replaceSourcesWithMarkdownLinks = (textWithSources: string): string => {
        const regex = /\((?:Text)?(\d+(?:\s*,\s*(?:Text)?\d+)*)\)|\[(\d+)\]/g;
        return textWithSources.replace(regex, (match, p1, p2) => {
          const numbers = (p1 || p2 || match).match(/\d+/g);
          if (!numbers) return match;
          const links = numbers.map((numStr: string) => {
            const n = parseInt(numStr, 10);
            const url = buildSourceUrl(n);
            // Make the entire token, including parentheses, clickable
            return url ? `[(${n})](${url})` : `(${n})`;
          });
          // Separate adjacent links with a space to increase clickable area
          return `${links.join(' ')}`;
        });
      };

      const replaceSourcesWithHTMLLinks = (textWithSources: string): string => {
        const regex = /\((?:Text)?(\d+(?:\s*,\s*(?:Text)?\d+)*)\)|\[(\d+)\]/g;
        return textWithSources.replace(regex, (match, p1, p2) => {
          const numbers = (p1 || p2 || match).match(/\d+/g);
          if (!numbers) return match;
          const links = numbers.map((numStr: string) => {
            const n = parseInt(numStr, 10);
            const url = buildSourceUrl(n);
            // Make the entire token, including parentheses, clickable
            return url ? `<a href="${url}">(${n})</a>` : `(${n})`;
          });
          // Separate adjacent links with a space to increase clickable area
          return `${links.join(' ')}`;
        });
      };

      const buildHTMLTableFromMarkdown = (tableMarkdown: string): string => {
        const lines = tableMarkdown.split('\n').filter((l) => l.trim().startsWith('|'));
        if (lines.length === 0) return '';

        // Remove possible separator row (2nd line) like | --- | --- |
        const rows = lines.map((line) =>
          line
            .trim()
            .replace(/^\|/, '')
            .replace(/\|$/, '')
            .split('|')
            .map((cell) => cell.trim()),
        );

        const hasSeparator = rows.length > 1 && rows[1].every((cell) => /^:?-{3,}:?$/.test(cell));
        const header = rows[0];
        const dataRows = hasSeparator ? rows.slice(2) : rows.slice(1);

        const headerHtml =
          '<thead><tr>' +
          header
            .map(
              (h) =>
                `<th style="border: 1px solid #000; padding: 2px 4px; background-color: #f0f0f0; font-weight: bold; text-align: left; line-height: 1.2;">${replaceSourcesWithHTMLLinks(
                  h,
                )}</th>`,
            )
            .join('') +
          '</tr></thead>';

        const bodyHtml =
          '<tbody>' +
          dataRows
            .map(
              (row) =>
                '<tr style="border-bottom: 1px solid #ddd;">' +
                row
                  .map(
                    (c) =>
                      `<td style="border: 1px solid #000; padding: 2px 2px; line-height: 1.2;">${replaceSourcesWithHTMLLinks(
                        c,
                      )}</td>`,
                  )
                  .join('') +
                '</tr>',
            )
            .join('') +
          '</tbody>';

        const table =
          '<table style="border-collapse: collapse; border: 1px solid #000; font-family: Arial, sans-serif; font-size: 11px; width: 100%;">' +
          headerHtml +
          bodyHtml +
          '</table>';
        return table;
      };

      // Fallback: if no raw markdown or table not found, generate markdown from table
      if (!markdownText) {
        const rows = tableElement.querySelectorAll('tr');
        const markdownRows: string[] = [];

        rows.forEach((row, rowIndex) => {
          const cells = row.querySelectorAll('th, td');
          const cellTexts: string[] = [];

          cells.forEach((cell) => {
            // Get text content and clean it up, escape pipe characters
            const text = cell.textContent?.replace(/\s+/g, ' ').trim().replace(/\|/g, '\\|') || '';
            cellTexts.push(text);
          });

          // Create markdown table row
          const rowText = '| ' + cellTexts.join(' | ') + ' |';
          markdownRows.push(rowText);

          // Add separator row after header (first row)
          if (rowIndex === 0 && row.querySelector('th')) {
            const separatorCells = cellTexts.map(() => '---');
            const separatorRow = '| ' + separatorCells.join(' | ') + ' |';
            markdownRows.push(separatorRow);
          }
        });

        markdownText = markdownRows.join('\n');
      }

      // If we were able to capture markdown, prefer generating a hyperlinked HTML table from it
      let hyperlinkedTableHTML = '';
      let markdownWithLinks = '';
      if (markdownText) {
        markdownWithLinks = replaceSourcesWithMarkdownLinks(markdownText);
        hyperlinkedTableHTML = buildHTMLTableFromMarkdown(markdownText);
      }

      // Create a styled version of the table HTML for clipboard with minimal padding
      const styledTableHTML = (hyperlinkedTableHTML || tableHTML)
        .replace(
          /<table[^>]*>/i,
          '<table style="border-collapse: collapse; border: 1px solid #000; font-family: Arial, sans-serif; font-size: 11px;">',
        )
        .replace(
          /<th[^>]*>/gi,
          '<th style="border: 1px solid #000; padding: 2px 4px; background-color: #f0f0f0; font-weight: bold; text-align: left; line-height: 1.2;">',
        )
        .replace(
          /<td[^>]*>/gi,
          '<td style="border: 1px solid #000; padding: 2px 4px; line-height: 1.2;">',
        );

      // Try to copy both HTML and plain text to clipboard
      if (navigator.clipboard && navigator.clipboard.write) {
        // Modern clipboard API that supports multiple formats
        const clipboardItems = [
          new ClipboardItem({
            'text/html': new Blob([styledTableHTML], { type: 'text/html' }),
            'text/plain': new Blob([markdownWithLinks || markdownText], { type: 'text/plain' }),
          }),
        ];

        await navigator.clipboard.write(clipboardItems);
      } else if (navigator.clipboard && navigator.clipboard.writeText) {
        // Fallback to plain text only
        await navigator.clipboard.writeText(markdownWithLinks || markdownText);
      } else {
        throw new Error('Clipboard API not supported');
      }
    } catch (error) {
      console.error('Failed to copy table:', error);

      // Fallback for older browsers using document.execCommand
      try {
        // Create a temporary container
        const container = document.createElement('div');
        container.style.position = 'fixed';
        container.style.left = '-9999px';
        container.style.top = '-9999px';

        // Clone the table and clean it up for copying
        const clonedTable = tableElement.cloneNode(true) as HTMLTableElement;

        // Remove the copy button from the cloned table
        const copyButton = clonedTable.querySelector('.table-copy-button');
        if (copyButton) {
          copyButton.remove();
        }

        // Style the cloned table for better copying
        clonedTable.style.borderCollapse = 'collapse';
        clonedTable.style.border = '1px solid #000';

        // Style all cells with minimal padding for compact pasting
        const allCells = clonedTable.querySelectorAll('th, td');
        allCells.forEach((cell) => {
          (cell as HTMLElement).style.border = '1px solid #000';
          (cell as HTMLElement).style.padding = '2px 4px';
          (cell as HTMLElement).style.lineHeight = '1.2';
        });

        container.appendChild(clonedTable);
        document.body.appendChild(container);

        // Select the content
        const range = document.createRange();
        range.selectNodeContents(clonedTable);
        const selection = window.getSelection();
        selection?.removeAllRanges();
        selection?.addRange(range);

        // Copy to clipboard
        const success = document.execCommand('copy');

        // Clean up
        selection?.removeAllRanges();
        document.body.removeChild(container);

        if (success) {
          console.log('Table copied to clipboard (fallback with formatting)');
        } else {
          throw new Error('execCommand failed');
        }
      } catch (fallbackError) {
        console.error('Fallback copy also failed:', fallbackError);

        // Final fallback - plain text only
        try {
          const textArea = document.createElement('textarea');

          // Generate markdown as final fallback
          const rows = tableElement.querySelectorAll('tr');
          const markdownRows: string[] = [];

          rows.forEach((row, rowIndex) => {
            const cells = row.querySelectorAll('th, td');
            const cellTexts: string[] = [];

            cells.forEach((cell) => {
              const text =
                cell.textContent?.replace(/\s+/g, ' ').trim().replace(/\|/g, '\\|') || '';
              cellTexts.push(text);
            });

            const rowText = '| ' + cellTexts.join(' | ') + ' |';
            markdownRows.push(rowText);

            if (rowIndex === 0 && row.querySelector('th')) {
              const separatorCells = cellTexts.map(() => '---');
              const separatorRow = '| ' + separatorCells.join(' | ') + ' |';
              markdownRows.push(separatorRow);
            }
          });

          textArea.value = markdownRows.join('\n') || 'Failed to copy table';
          document.body.appendChild(textArea);
          textArea.select();
          document.execCommand('copy');
          document.body.removeChild(textArea);
        } catch (finalError) {
          console.error('All copy methods failed:', finalError);
        }
      }
    }
  };

  // (Removed unused generic copyElementToClipboard helper)

  // Helper function to parse markdown-style text and convert to JSX
  const parseMarkdownText = (text: string, sourceBtns?: React.ReactNode) => {
    if (!text) return null;

    // Split by lines for processing
    const lines = text.split('\n');
    const elements: JSX.Element[] = [];
    let key = 0;

    lines.forEach((line, index) => {
      // Skip empty lines at the beginning but preserve spacing between content
      if (line.trim() === '') return;

      // Headers (## or ###)
      if (line.startsWith('### ')) {
        const headerText = line.replace(/^### \*\*(.*?)\*\*$/, '$1').replace(/^### /, '');
        elements.push(
          <h4
            key={`h4-${key++}`}
            style={{
              fontSize: '13px',
              fontWeight: 'bold',
              margin: '12px 0 8px 0',
              color: '#333',
            }}
          >
            {headerText}
          </h4>,
        );
      } else if (line.startsWith('## ')) {
        const headerText = line.replace(/^## \*\*(.*?)\*\*$/, '$1').replace(/^## /, '');
        elements.push(
          <h3
            key={`h3-${key++}`}
            style={{
              fontSize: '14px',
              fontWeight: 'bold',
              margin: '20px 0 20px 0',
              color: '#333',
            }}
          >
            {headerText}
          </h3>,
        );
      }
      // Horizontal rule
      else if (line.trim() === '---') {
        elements.push(
          <hr
            key={`hr-${key++}`}
            style={{
              margin: '16px 0',
              border: 'none',
              borderTop: '1px solid #ddd',
            }}
          />,
        );
      }
      // Bullet points
      else if (line.startsWith('- ')) {
        const bulletText = line.substring(2);
        const formattedText = processBoldText(bulletText);

        // Check if this is the last non-empty line
        const isLastLine =
          index === lines.length - 1 || lines.slice(index + 1).every((l) => l.trim() === '');

        elements.push(
          <div
            key={`bullet-${key++}`}
            style={{
              marginLeft: '20px',
              marginBottom: '4px',
              position: 'relative',
              display: isLastLine && sourceBtns ? 'inline-block' : 'block',
            }}
          >
            <FiberManualRecord
              style={{
                position: 'absolute',
                left: '-18px',
                top: '2px',
                fontSize: '8px',
                color: '#666',
              }}
            />
            {formattedText}
            {isLastLine && sourceBtns && <span style={{ marginLeft: '4px' }}>{sourceBtns}</span>}
          </div>,
        );
      }
      // Regular text
      else {
        const formattedText = processBoldText(line);
        if (formattedText) {
          // Check if this is the last non-empty line
          const isLastLine =
            index === lines.length - 1 || lines.slice(index + 1).every((l) => l.trim() === '');

          elements.push(
            <div
              key={`text-${key++}`}
              style={{
                marginBottom: '4px',
                display: isLastLine && sourceBtns ? 'inline-block' : 'block',
              }}
            >
              {formattedText}
              {isLastLine && sourceBtns && <span style={{ marginLeft: '4px' }}>{sourceBtns}</span>}
            </div>,
          );
        }
      }
    });

    return <div>{elements}</div>;
  };

  // Helper function to process bold text (**text**)
  const processBoldText = (text: string) => {
    if (!text) return null;

    const parts = text.split(/(\*\*.*?\*\*)/g);
    return parts.map((part, index) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        const boldText = part.slice(2, -2);
        return (
          <strong key={`bold-${index}`} style={{ fontWeight: 'bold' }}>
            {boldText}
          </strong>
        );
      }
      return part;
    });
  };
  const handleItemClick = (item: any) => {
    const getHighlightColorForFile = (id: string): string | undefined => {
      if (!compare?.files?.length) return undefined;
      if (compare.files[0] && id === compare.files[0]) return DEFAULT_NAVIGATION_HIGHLIGHT_COLOR;
      // Check if file is in any compare position (indices 1, 2, 3, ...)
      if (compare.files.length > 1 && compare.files.slice(1).includes(id))
        return COMPARE_NAVIGATION_HIGHLIGHT_COLOR;
      return undefined;
    };
    let currentFileId = '';
    const currentPathname = history.location.pathname;
    if (currentPathname.includes('/files/')) {
      const pathSegments = currentPathname.split('/');
      currentFileId = pathSegments[pathSegments.length - 1];
    }
    if (!fileSidebarAskAI) {
      const newLocation = {
        ...item.location,
        timestamp: Date.now(),
      };
      if (compare?.files?.length > 1 && currentFileId && item.fileId !== currentFileId) {
        // Load file details for comparison and dispatch setFileToCompare
        loadFileWithCache(
          item.fileId,
          (fileDetails) => {
            dispatch(setSidebarOpen(false));
            dispatch(setFileToCompare(fileDetails));
            // Add a delay before dispatching setDocSearchForCompare to ensure file loading is complete
            setTimeout(() => {
              dispatch(
                setDocSearchForCompare(item.highlight ? item.highlight : item.text, {
                  ...newLocation,
                  isCompare: true,
                  highlightColor: getHighlightColorForFile(item.fileId || item.file?.id),
                }),
              );
            }, 1000);
          },
          fileToCompareCache,
        );
      } else {
        history.push(
          history.location.pathname.replace(/\/files.*/, ``) +
            `/files/${item.fileId || item.file.id}?askAI=true`,
        );

        // Load file details using common function
        loadFileWithCache(item.fileId, (fileDetails) => {
          dispatch(setCurrentSelectedFile(fileDetails));
        });
        // Skip dispatch timing if this is a title-card citation (no location navigation needed)
        if (item.citationType !== 'title-card') {
          // Add a small delay before dispatching setDocSearch to ensure file loading is complete
          setTimeout(() => {
            // If this file is any non-anchor compare file, mark location as compare to use green highlight
            const isGreenCompareFile =
              compare?.files?.length > 1 &&
              compare.files.slice(1).includes(item.fileId || item.file?.id);
            const highlightColor = getHighlightColorForFile(item.fileId || item.file?.id);
            dispatch(
              setDocSearch(item.highlight ? item.highlight : item.text, {
                ...newLocation,
                isCompare: !!isGreenCompareFile,
                ...(highlightColor ? { highlightColor } : {}),
              }),
            );
          }, 200);
        }
      }
    } else {
      // Use the same dispatch approach for fileSidebarAskAI context
      const newLocation = {
        ...item.location,
        // Add a timestamp to ensure the object is always new
        timestamp: Date.now(),
      };
      if (gridView) {
        const urlParams = new URLSearchParams(window.location.search);
        const isAskAI = urlParams.get('askAI');
        if (compare?.files?.length > 1 && currentFileId && item.fileId !== currentFileId) {
          // Load file details for comparison and dispatch setFileToCompare
          loadFileWithCache(
            item.fileId,
            (fileDetails) => {
              dispatch(setFileToCompare(fileDetails));
              // Add a delay before dispatching setDocSearchForCompare to ensure file loading is complete
              setTimeout(() => {
                dispatch(
                  setDocSearchForCompare(item.highlight ? item.highlight : item.text, {
                    ...newLocation,
                    isCompare: true,
                    highlightColor: getHighlightColorForFile(item.fileId || item.file?.id),
                  }),
                );
              }, 1000);
            },
            fileToCompareCache,
          );
        } else if (compare?.files?.length > 1 || !currentFileId || (currentFileId && isAskAI)) {
          // Extract current file ID from pathname
          // Check if the new file ID is different from current
          if (item.fileId && item.fileId !== currentFileId) {
            let newPathname;
            let urlWithParams;
            if (!currentFileId) {
              // If no current file ID, append /files/{item.fileId} to the current pathname
              newPathname = `${currentPathname}/files/${item.fileId}`;
              const primaryFile = compare?.files[0];
              urlWithParams = primaryFile
                ? `${newPathname}?primaryFile=${primaryFile}&askAI=true`
                : `${newPathname}?askAI=true`;
            } else {
              // Update URL with new file ID (existing logic)
              newPathname = currentPathname.replace(currentFileId, item.fileId);
              const primaryFile = compare?.files[0];
              urlWithParams = primaryFile
                ? `${newPathname}?primaryFile=${primaryFile}&askAI=true`
                : `${newPathname}?askAI=true`;
            }

            history.push(urlWithParams);

            // Handle file details caching and loading for the new file using common function
            loadFileWithCache(item.fileId, (fileDetails) => {
              dispatch(setCurrentSelectedFile(fileDetails));
              dispatch(setSidebarOpen(false));
              // Dispatch setDocSearch after the file is successfully loaded
              setTimeout(() => {
                const isGreenCompareFile =
                  compare?.files?.length > 1 && compare.files.slice(1).includes(item.fileId);
                const highlightColor = getHighlightColorForFile(item.fileId);
                dispatch(
                  setDocSearch(item.highlight ? item.highlight : item.text, {
                    ...newLocation,
                    isCompare: !!isGreenCompareFile,
                    ...(highlightColor ? { highlightColor } : {}),
                  }),
                );
              }, 100);
            });
          } else {
            // Same file, just dispatch setDocSearch immediately
            setTimeout(() => {
              const isGreenCompareFile =
                compare?.files?.length > 1 && compare.files.slice(1).includes(item.fileId);
              const highlightColor = getHighlightColorForFile(item.fileId);
              dispatch(
                setDocSearch(item.highlight ? item.highlight : item.text, {
                  ...newLocation,
                  isCompare: !!isGreenCompareFile,
                  ...(highlightColor ? { highlightColor } : {}),
                }),
              );
            }, 100);
          }
        } else {
          // No compare files, dispatch setDocSearch immediately
          setTimeout(() => {
            const isGreenCompareFile =
              compare?.files?.length > 1 && compare.files.slice(1).includes(item.fileId);
            const highlightColor = getHighlightColorForFile(item.fileId);
            dispatch(
              setDocSearch(item.highlight ? item.highlight : item.text, {
                ...newLocation,
                isCompare: !!isGreenCompareFile,
                ...(highlightColor ? { highlightColor } : {}),
              }),
            );
          }, 100);
        }
      } else {
        // Use dispatch setDocSearch instead of highlightLocation
        const enhancedLocation = {
          ...newLocation,
          showMarkButton: fileSidebarAskAI || window.location.href.includes('ai-tools'),
        };
        if (compare?.files?.length > 1 && currentFileId && item.fileId !== currentFileId) {
          // Load file details for comparison and dispatch setFileToCompare
          loadFileWithCache(
            item.fileId,
            (fileDetails) => {
              dispatch(setFileToCompare(fileDetails));
              // Add a delay before dispatching setDocSearchForCompare to ensure file loading is complete
              setTimeout(() => {
                dispatch(
                  setDocSearchForCompare(item.highlight ? item.highlight : item.text, {
                    ...enhancedLocation,
                    isCompare: true,
                    highlightColor: getHighlightColorForFile(item.fileId || item.file?.id),
                  }),
                );
              }, 1000);
            },
            fileToCompareCache,
          );
        } else {
          const isGreenCompareFile =
            compare?.files?.length > 1 &&
            compare.files.slice(1).includes(item.fileId || item.file?.id);
          const highlightColor = getHighlightColorForFile(item.fileId || item.file?.id);
          dispatch(
            setDocSearch(item.highlight ? item.highlight : item.text, {
              ...enhancedLocation,
              isCompare: !!isGreenCompareFile,
              ...(highlightColor ? { highlightColor } : {}),
            }),
          );
        }
      }
    }
  };
  const getSourcesBtns = (refrences: any, lineIndex: number) => {
    return refrences.map(
      (src: any, srcIdx: number) =>
        (src.source || src) <= allMatchesCount && (
          <React.Fragment key={srcIdx}>
            (
            {(() => {
              const sourceNum = src.source || src;
              const matchData = allMatches && allMatches[src.source - 1];
              const citationType = matchData?.citationType;
              const isTitleCard = citationType === 'title-card';

              return (
                <Tooltip
                  title={
                    allMatches && allMatches[src.source - 1] ? (
                      <div>
                        {/* {isTitleCard && (
                          <div style={{ marginBottom: '12px', fontSize: '14px' }}>
                            {T.translate('generic.fileContentInfoSource')}
                          </div>
                        )} */}
                        {/* {!isTitleCard && ( */}
                        <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                          <Markdown remarkPlugins={[remarkGfm]}>
                            {allMatches[src.source - 1].text}
                          </Markdown>
                        </div>
                        {/* )} */}
                        <div style={{ marginTop: '12px', fontSize: '14px' }}>
                          <div style={{ fontWeight: 'bold', fontSize: '12px' }}>
                            {T.translate('case.documentName')}:{'  '}
                            {allMatches[src.source - 1].fileName ||
                              allMatches[src.source - 1].filename ||
                              allMatches[src.source - 1].file?.name}
                          </div>
                          <div style={{ fontWeight: 'bold', fontSize: '12px' }}>
                            {T.translate('case.documentIdFull')}:{'  '}
                            {allMatches[src.source - 1].fileId ||
                              allMatches[src.source - 1].file?.id}
                          </div>
                          {allMatches[src.source - 1]?.location?.pageNumber > 0 && (
                            <div style={{ fontWeight: 'bold', fontSize: '12px' }}>
                              {T.translate('case.pageNumber')}:{'  '}
                              {allMatches[src.source - 1]?.location?.pageNumber}
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      `Source ${src.source || src}`
                    )
                  }
                  placement="top"
                  arrow
                  componentsProps={{
                    tooltip: {
                      className: (() => {
                        if (isTitleCard) {
                          return 'tooltip-title-card';
                        }
                        if (compare?.files?.length > 0 && allMatches && allMatches[sourceNum - 1]) {
                          const fileId =
                            allMatches[sourceNum - 1].fileId || allMatches[sourceNum - 1].file?.id;
                          if (fileId === compare.files[0]) {
                            return 'tooltip-first-file';
                          } else if (
                            compare.files.length > 1 &&
                            compare.files.slice(1).includes(fileId)
                          ) {
                            return 'tooltip-second-file';
                          }
                        }
                        return 'tooltip-default';
                      })(),
                      sx: {
                        maxHeight: '400px',
                        maxWidth: '600px',
                        overflowY: 'auto',
                        zIndex: 100,
                        '&::-webkit-scrollbar': {
                          width: '8px',
                          opacity: 0,
                          transition: 'opacity 0.3s',
                        },
                        '&::-webkit-scrollbar-track': {
                          background: 'transparent',
                        },
                        '&::-webkit-scrollbar-thumb': {
                          background: 'transparent',
                          borderRadius: '4px',
                        },
                        '&:hover::-webkit-scrollbar-thumb': {
                          background: '#888',
                        },
                        '&:hover::-webkit-scrollbar-thumb:hover': {
                          background: '#555',
                        },
                        scrollbarWidth: 'thin',
                        scrollbarColor: 'transparent transparent',
                        '&:hover': {
                          scrollbarColor: '#888 transparent',
                        },
                      },
                    },
                  }}
                >
                  <Button
                    size="small"
                    sx={{
                      p: 0,
                      minWidth: '0.5rem',
                      fontSize: isTranscriptPage ? '1.3rem' : '1rem',
                      fontWeight: clickedSource === `${lineIndex}-${src.source || src}` ? 700 : 600,
                      textDecoration:
                        clickedSource === `${lineIndex}-${src.source || src}`
                          ? 'underline'
                          : 'none',
                      textDecorationColor:
                        clickedSource === `${lineIndex}-${src.source || src}`
                          ? '#233473'
                          : 'transparent',
                      textDecorationThickness:
                        clickedSource === `${lineIndex}-${src.source || src}` ? '2px' : '1px',
                      color: isTitleCard ? 'inherit' : '#233473',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '2px',
                      '&:disabled': isTitleCard ? {} : { color: '#ccc' },
                    }}
                    variant="text"
                    onClick={(e) => {
                      selectText(lineIndex);
                      // Set the clicked source to show visual feedback
                      setAIClickedSource(`${lineIndex}-${src.source || src}`);

                      // Handle direct navigation instead of opening menu
                      if (allMatches && allMatches[src.source - 1]) {
                        const match = {
                          ...allMatches[src.source - 1],
                          srcIndex: src.source || src,
                          highlight: src.highlight || allMatches[src.source - 1].highlight || '',
                        };
                        handleItemClick(match);
                      } else if (handleMatchesLinkClicked) {
                        // Fallback to old behavior if needed
                        handleMatchesLinkClicked(
                          e,
                          src.source || src,
                          allMatches,
                          src.highlight || '',
                        );
                      }
                    }}
                  >
                    {isTitleCard && <Description sx={{ fontSize: '0.9em', marginRight: '-2px' }} />}
                    {src.source || src}
                  </Button>
                </Tooltip>
              );
            })()}
            )
          </React.Fragment>
        ),
    );
  };

  // Alternative method to render text with Markdown-compatible source links
  const renderTextWithSourceButtons = (text: string) => {
    const sanitizeMarkdownTitle = (title: string): string => {
      if (!title) return '';
      return String(title)
        .replace(/\r?\n|\r/g, ' ')
        .replace(/"/g, "'")
        .replace(/\\/g, '/')
        .replace(/\s+/g, ' ')
        .trim();
    };
    // Avoid processing if text already contains markdown links (prevent double processing)
    if (
      text.includes('](#source-') ||
      text.includes('](#expand-sources-') ||
      text.includes('](#collapse-sources-')
    ) {
      return text;
    }

    // Match patterns like (Text175, Text176), (Text2), or [1]
    // More comprehensive regex to handle various formats
    const regex = /\((?:Text)?(\d+(?:\s*,\s*(?:Text)?\d+)*)\)|\[(\d+)\]/g;

    return text.replace(regex, (match, p1, p2) => {
      // Extract numbers from the match - handle both "Text123" and "123" formats
      const numbers = (p1 || p2 || match).match(/\d+/g);
      if (!numbers) {
        return match;
      }

      // Create a unique key for this source group - use a stable identifier
      const sortedNumbers = [...numbers].sort((a, b) => parseInt(a) - parseInt(b));
      const sourceGroupKey = `sources-${sortedNumbers.join('-')}`;
      const isExpanded = expandedSources.has(sourceGroupKey);

      // Show first 3 or all sources based on expansion state
      const sourcesToShow = isExpanded ? numbers : numbers.slice(0, 3);
      const remaining = isExpanded ? [] : numbers.slice(3);

      // Create Markdown links for sources to show
      const markdownLinks = sourcesToShow.map((num: string) => {
        const sourceNum = parseInt(num, 10);

        // Check if we have match data for tooltip-like behavior
        if (allMatches && allMatches[sourceNum - 1]) {
          const matchData = allMatches[sourceNum - 1];
          const fileName =
            matchData.fileName || matchData.filename || matchData.file?.name || 'Unknown';
          const fileId = matchData.fileId || matchData.file?.id || 'Unknown';

          // Create a Markdown link with title attribute for hover effect
          const safeTitle = `${sanitizeMarkdownTitle(fileName)} - ID: ${sanitizeMarkdownTitle(
            String(fileId),
          )}`;
          return `[${sourceNum}](#source-${sourceNum} "${safeTitle}")`;
        } else {
          // Fallback for sources without match data
          return `[${sourceNum}](#source-${sourceNum} "Source ${sourceNum}")`;
        }
      });

      // Add "+X more" link if there are more than 3 sources and not expanded
      if (remaining.length > 0) {
        markdownLinks.push(
          `[+${remaining.length} More ](#expand-sources-${sourceGroupKey} "Click to see ${remaining.length} more sources")`,
        );
      }

      // Return the Markdown links joined with commas, wrapped in parentheses
      let result;
      if (isExpanded && numbers.length > 3) {
        // Add collapse icon with space but no comma
        const hiddenCount = numbers.length - 3;
        result = `(${markdownLinks.join(', ')} [⇇](#collapse-sources-${sourceGroupKey} "Click to hide ${hiddenCount} sources"))`;
      } else {
        result = `(${markdownLinks.join(', ')})`;
      }

      return result;
    });
  };
  const renderRawText = (text: string) => {
    // Build a URL for a given source number using allMatches and caseId
    const buildSourceUrl = (sourceNum: number): string | null => {
      const matchData = allMatches && allMatches[sourceNum - 1];
      if (!matchData) return null;
      const fileId = matchData.fileId || matchData.file?.id;
      const currentPageNumber = matchData.location?.pageNumber;
      if (!fileId || !currentPageNumber) return null;
      const url = `${window.location.origin}/case/${caseId}/files/${fileId}?goToPage=${currentPageNumber}`;
      return url;
    };

    // Expand all sources and convert them to markdown hyperlinks for copying
    const expandAndHyperlinkSourcesMarkdown = (markdownText: string): string => {
      if (!markdownText) return markdownText;
      const regex = /\((?:Text)?(\d+(?:\s*,\s*(?:Text)?\d+)*)\)|\[(\d+)\]/g;
      return markdownText.replace(regex, (match, p1, p2) => {
        const numbers = (p1 || p2 || match).match(/\d+/g);
        if (!numbers) return match;
        const links = numbers.map((numStr: string) => {
          const n = parseInt(numStr, 10);
          const url = buildSourceUrl(n);
          // Make the entire token, including parentheses, clickable
          return url ? `[(${n})](${url})` : `(${n})`;
        });
        // Separate adjacent links with a space to increase clickable area
        return `${links.join(' ')}`;
      });
    };

    // Very basic markdown-to-HTML conversion for links, headers and lists
    const markdownToBasicHTML = (markdownText: string): string => {
      const escapeHtml = (s: string) =>
        s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

      const toInlineHTML = (s: string) => {
        // bold
        let t = s.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
        // links [text](url)
        t = t.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>');
        return t;
      };

      const lines = markdownText.split('\n');
      const htmlParts: string[] = [];
      let inList = false;

      for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        if (!line.trim()) {
          if (inList) {
            htmlParts.push('</ul>');
            inList = false;
          }
          continue;
        }
        if (line.startsWith('### ')) {
          const headerText = line.replace(/^### \*\*(.*?)\*\*$/, '$1').replace(/^### /, '');
          htmlParts.push(`<h4>${toInlineHTML(escapeHtml(headerText))}</h4>`);
          continue;
        }
        if (line.startsWith('## ')) {
          const headerText = line.replace(/^## \*\*(.*?)\*\*$/, '$1').replace(/^## /, '');
          htmlParts.push(`<h3>${toInlineHTML(escapeHtml(headerText))}</h3>`);
          continue;
        }
        if (line.trim() === '---') {
          htmlParts.push('<hr/>');
          continue;
        }
        if (line.startsWith('- ')) {
          if (!inList) {
            inList = true;
            htmlParts.push('<ul>');
          }
          const bullet = line.substring(2);
          htmlParts.push(`<li>${toInlineHTML(escapeHtml(bullet))}</li>`);
          continue;
        }
        if (inList) {
          htmlParts.push('</ul>');
          inList = false;
        }
        htmlParts.push(`<div>${toInlineHTML(escapeHtml(line))}</div>`);
      }
      if (inList) htmlParts.push('</ul>');
      return htmlParts.join('');
    };

    // Copy answer content with fully expanded, hyperlinked sources
    const copyAnswerWithHyperlinks = async (rawMarkdown: string) => {
      try {
        const copyMarkdown = expandAndHyperlinkSourcesMarkdown(rawMarkdown);
        const htmlContent = markdownToBasicHTML(copyMarkdown);
        const plainText = copyMarkdown.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '$1 ($2)');
        if (navigator.clipboard && navigator.clipboard.write) {
          const clipboardItems = [
            new ClipboardItem({
              'text/html': new Blob([htmlContent], { type: 'text/html' }),
              'text/plain': new Blob([plainText], { type: 'text/plain' }),
            }),
          ];
          await navigator.clipboard.write(clipboardItems);
          return;
        }

        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(plainText);
          return;
        }

        throw new Error('Clipboard API not supported');
      } catch {
        // Final fallback via textarea
        try {
          const copyMarkdown = expandAndHyperlinkSourcesMarkdown(rawMarkdown);
          const plainText = copyMarkdown.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '$1 ($2)');
          const textArea = document.createElement('textarea');
          textArea.value = plainText;
          document.body.appendChild(textArea);
          textArea.select();
          document.execCommand('copy');
          document.body.removeChild(textArea);
        } catch (finalError) {
          console.error('Failed to copy answer with hyperlinks:', finalError);
        }
      }
    };
    // Process the text with source references based on current expansion state
    const processedText = renderTextWithSourceButtons(text);

    // Custom link renderer to handle source clicks
    const customLinkRenderer = ({ href, children, title }: any) => {
      // Check if this is an "expand sources" link
      if (href && href.startsWith('#expand-sources-')) {
        const sourceGroupKey = href.replace('#expand-sources-', '');

        return (
          <Button
            size="small"
            sx={{
              p: '0 5px',
              minWidth: '0.5rem',
              fontSize: isTranscriptPage ? '1.3rem' : '1rem',
              fontWeight: 600,
              color: '#4567E5',
              textTransform: 'none', // Prevent uppercase transformation
              '&:hover': {
                backgroundColor: '#E5E5E5',
              },
            }}
            variant="text"
            onClick={(e) => {
              e.preventDefault();
              setExpandedSources((prev) => {
                const newSet = new Set(prev);
                newSet.add(sourceGroupKey);
                return newSet;
              });
            }}
          >
            {children}
          </Button>
        );
      }

      // Check if this is a "collapse sources" link
      if (href && href.startsWith('#collapse-sources-')) {
        const sourceGroupKey = href.replace('#collapse-sources-', '');

        return (
          <Button
            size="small"
            sx={{
              p: '0 5px',
              minWidth: '0.5rem',
              fontSize: isTranscriptPage ? '1.3rem' : '1rem',
              fontWeight: 600,
              color: '#4567E5',
              textTransform: 'none', // Prevent uppercase transformation
              '&:hover': {
                backgroundColor: '#E5E5E5',
              },
              display: 'inline-flex',
              alignItems: 'center',
              gap: '2px',
            }}
            variant="text"
            onClick={(e) => {
              e.preventDefault();
              setExpandedSources((prev) => {
                const newSet = new Set(prev);
                newSet.delete(sourceGroupKey);
                return newSet;
              });
            }}
          >
            {children?.toString().includes('⇇') ? (
              <>
                <KeyboardDoubleArrowLeft sx={{ fontSize: 'inherit' }} />
              </>
            ) : (
              children
            )}
          </Button>
        );
      }

      // Check if this is a regular source link
      if (href && href.startsWith('#source-')) {
        const sourceNum = parseInt(href.replace('#source-', ''), 10);

        // Check if location is valid (not empty, null, undefined, or empty object {})
        const matchData = allMatches && allMatches[sourceNum - 1];
        const isLocationValid =
          matchData &&
          (matchData?.citationType === 'title-card' ||
            (matchData.location &&
              !(
                typeof matchData.location === 'object' &&
                Object.keys(matchData.location).length === 0
              )));

        // Get citation type
        const citationType = matchData?.citationType;
        const isTitleCard = citationType === 'title-card';

        // Create a unique identifier for this source button
        const sourceId = `markdown-${sourceNum}`;

        return (
          <Tooltip
            title={
              allMatches && allMatches[sourceNum - 1] ? (
                <div>
                  {/* {isTitleCard && (
                    <div style={{ marginBottom: '12px', fontSize: '14px' }}>
                      {T.translate('generic.fileContentInfoSource')}
                    </div>
                  )} */}
                  {/* {!isTitleCard && ( */}
                  <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    <Markdown remarkPlugins={[remarkGfm]}>
                      {allMatches[sourceNum - 1].text}
                    </Markdown>
                  </div>
                  {/* )} */}
                  <div style={{ marginTop: '12px', fontSize: '14px' }}>
                    <div>
                      {T.translate('case.documentName')}:{'  '}
                      {allMatches[sourceNum - 1].fileName ||
                        allMatches[sourceNum - 1].filename ||
                        allMatches[sourceNum - 1].file?.name}
                    </div>
                    <div>
                      {T.translate('case.documentIdFull')}:{'  '}
                      {allMatches[sourceNum - 1].fileId || allMatches[sourceNum - 1].file?.id}
                    </div>
                    {allMatches[sourceNum - 1]?.location?.pageNumber > 0 && (
                      <div>
                        {T.translate('case.pageNumber')}:{'  '}
                        {allMatches[sourceNum - 1]?.location?.pageNumber}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                `Source ${sourceNum}`
              )
            }
            placement="top"
            arrow
            componentsProps={{
              tooltip: {
                className: (() => {
                  if (isTitleCard) {
                    return 'tooltip-title-card';
                  }
                  if (compare?.files?.length > 0 && allMatches && allMatches[sourceNum - 1]) {
                    const fileId =
                      allMatches[sourceNum - 1].fileId || allMatches[sourceNum - 1].file?.id;
                    if (fileId === compare.files[0]) {
                      return 'tooltip-first-file';
                    } else if (
                      compare.files.length > 1 &&
                      compare.files.slice(1).includes(fileId)
                    ) {
                      return 'tooltip-second-file';
                    }
                  }
                  return 'tooltip-default';
                })(),
                sx: {
                  maxHeight: '400px',
                  maxWidth: '600px',
                  overflowY: 'auto',
                  zIndex: 100,
                  '&::-webkit-scrollbar': {
                    width: '8px',
                    opacity: 0,
                    transition: 'opacity 0.3s',
                  },
                  '&::-webkit-scrollbar-track': {
                    background: 'transparent',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: 'transparent',
                    borderRadius: '4px',
                  },
                  '&:hover::-webkit-scrollbar-thumb': {
                    background: '#888',
                  },
                  '&:hover::-webkit-scrollbar-thumb:hover': {
                    background: '#555',
                  },
                  scrollbarWidth: 'thin',
                  scrollbarColor: 'transparent transparent',
                  '&:hover': {
                    scrollbarColor: '#888 transparent',
                  },
                },
              },
            }}
          >
            <Button
              size="small"
              sx={{
                p: 0,
                minWidth: '0.5rem',
                fontSize: clickedSource === sourceId ? '1.3rem' : '1rem',
                border: clickedSource === sourceId ? '1px solid grey' : 'none',
                padding: clickedSource === sourceId ? '0 3px' : '0',
                lineHeight: '1.3rem !important',
                fontWeight: clickedSource === sourceId ? 700 : 600,
                color: isTitleCard
                  ? 'inherit'
                  : isLocationValid
                    ? (() => {
                        // Check if we have compare files and file ID matches any non-anchor compare file
                        if (compare?.files?.length > 1 && allMatches && allMatches[sourceNum - 1]) {
                          const fileId =
                            allMatches[sourceNum - 1].fileId || allMatches[sourceNum - 1].file?.id;
                          return compare.files.slice(1).includes(fileId) ? '#026C45' : '#4567E5';
                        }
                        return '#4567E5';
                      })()
                    : '#B3B3B3',
                textTransform: 'none', // Prevent uppercase transformation
                display: 'inline-flex',
                alignItems: 'center',
                gap: '2px',
                '&:hover': {
                  backgroundColor: isLocationValid ? 'rgba(25, 118, 210, 0.04)' : 'transparent',
                },
                cursor: isLocationValid ? 'pointer' : 'default',
                '&:disabled': isTitleCard ? {} : { color: '#ccc' },
              }}
              variant="text"
              onClick={(e) => {
                e.preventDefault();
                // Only handle source click if location is valid
                if (isLocationValid) {
                  // Set the clicked source to show visual feedback
                  setAIClickedSource(sourceId);
                  if (allMatches && allMatches[sourceNum - 1]) {
                    const match = {
                      ...allMatches[sourceNum - 1],
                      srcIndex: sourceNum,
                      highlight: allMatches[sourceNum - 1].highlight || '',
                    };
                    handleItemClick(match);
                  } else if (handleMatchesLinkClicked) {
                    handleMatchesLinkClicked(e, sourceNum, allMatches, '');
                  }
                }
              }}
            >
              {isTitleCard && <Description sx={{ fontSize: '0.9em', marginRight: '-2px' }} />}
              {children}
            </Button>
          </Tooltip>
        );
      }

      // Default link rendering for other links
      return (
        <a href={href} title={title}>
          {children}
        </a>
      );
    };

    // Custom table components for styling
    const customTableRenderer = ({ children }: any) => {
      return (
        <div className="table-container" style={{ overflowX: 'auto', paddingRight: '24px' }}>
          <IconButton
            className="table-copy-button"
            size="small"
            onClick={async () => {
              const tableElement = document.getElementById(tableId) as HTMLTableElement;
              if (tableElement) {
                await copyTableToClipboard(tableElement, text);
                setCopiedTable(true);
                // Revert tooltip text after a short delay
                setTimeout(() => setCopiedTable(false), 2000);
              }
            }}
            sx={{
              width: 28,
              height: 28,
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            <Tooltip
              title={copiedTable ? T.translate('generic.copied') : T.translate('generic.copy')}
              placement="top"
            >
              <ContentCopy sx={{ fontSize: 16, color: '#666' }} />
            </Tooltip>
          </IconButton>
          <table
            id={tableId}
            className="markdown-table"
            style={{
              width: '100%',
              fontSize: fileSidebarAskAI ? '12px' : '13px',
              borderCollapse: 'collapse',
              marginTop: '16px',
              marginBottom: '16px',
              border: '1px solid #ddd',
            }}
          >
            {children}
          </table>
        </div>
      );
    };

    const customTableHeadRenderer = ({ children }: any) => (
      <thead style={{ backgroundColor: '#f8f9fa' }}>{children}</thead>
    );

    const customTableBodyRenderer = ({ children }: any) => <tbody>{children}</tbody>;

    const customTableRowRenderer = ({ children }: any) => (
      <tr
        style={{
          borderBottom: '1px solid #ddd',
        }}
      >
        {children}
      </tr>
    );

    const customTableHeaderCellRenderer = ({ children }: any) => (
      <th
        style={{
          fontWeight: 'bold',
          padding: '3px',
          textAlign: 'left',
          borderRight: '1px solid #ddd',
          backgroundColor: '#f8f9fa',
          whiteSpace: 'normal',
        }}
      >
        {children}
      </th>
    );

    const customTableCellRenderer = ({ children }: any) => (
      <td
        style={{
          padding: '3px',
          borderRight: '1px solid #ddd',
          whiteSpace: 'normal',
        }}
      >
        {children}
      </td>
    );

    // Custom list renderers to reduce padding-left
    const customUnorderedListRenderer = ({ children }: any) => (
      <ul style={{ paddingLeft: '0.5rem', marginLeft: '1rem' }}>{children}</ul>
    );

    const customOrderedListRenderer = ({ children }: any) => (
      <ol style={{ paddingLeft: '0.5rem', marginLeft: '1rem' }}>{children}</ol>
    );

    const customDescriptionListRenderer = ({ children }: any) => (
      <dl style={{ paddingLeft: '0.5rem', marginLeft: '1rem' }}>{children}</dl>
    );

    return (
      <div className={`answer-container${hasTable ? ' has-table' : ''}`}>
        {!hasTable && (
          <IconButton
            className="answer-copy-button"
            size="small"
            onClick={async () => {
              if (answerContentRef.current) {
                await copyAnswerWithHyperlinks(text);
                setCopiedAnswer(true);
                // Revert tooltip text after a short delay
                setTimeout(() => setCopiedAnswer(false), 2000);
              }
            }}
            sx={{
              width: 28,
              height: 28,
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            <Tooltip
              title={copiedAnswer ? T.translate('generic.copied') : T.translate('generic.copy')}
              placement="top"
            >
              <ContentCopy sx={{ fontSize: 16, color: '#666' }} />
            </Tooltip>
          </IconButton>
        )}
        <div ref={answerContentRef}>
          <Markdown
            remarkPlugins={[remarkGfm]}
            components={{
              a: customLinkRenderer,
              table: customTableRenderer,
              thead: customTableHeadRenderer,
              tbody: customTableBodyRenderer,
              tr: customTableRowRenderer,
              th: customTableHeaderCellRenderer,
              td: customTableCellRenderer,
              ul: customUnorderedListRenderer,
              ol: customOrderedListRenderer,
              dl: customDescriptionListRenderer,
            }}
          >
            {processedText}
          </Markdown>
        </div>
      </div>
    );
  };
  return (
    <div
      style={{
        lineHeight: 1.4,
        paddingTop: 5,
        marginBottom: 0,
        paddingBottom: 0,
        position: 'relative',
        overflowX: 'auto',
        WebkitOverflowScrolling: 'touch',
      }}
    >
      <>
        {rawAnswer ? (
          renderRawText(rawAnswer)
        ) : (
          <div>
            {itemsToRender.map((item: any, ind: number) => (
              <div
                key={ind}
                style={{
                  marginBottom: ind < itemsToRender.length - 1 ? '12px' : '0',
                  paddingBottom: 0,
                  lineHeight: 'inherit',
                  wordWrap: 'break-word', // Handle long text better
                  overflowWrap: 'break-word',
                }}
              >
                {typingEffect ? (
                  <TypingEffect
                    responseRef={textRefs.current[ind]}
                    response={item.text}
                    typingSpeed={10}
                    scrollBarRef={scrollBarRef}
                    elementsAfterText={getSourcesBtns(item.references || item.sources, ind)}
                  />
                ) : (
                  <>
                    <div ref={textRefs.current[ind]} style={{ display: 'block' }}>
                      {parseMarkdownText(
                        item.text,
                        getSourcesBtns(item.references || item.sources, ind),
                      )}
                    </div>
                  </>
                )}
              </div>
            ))}
            {hasMoreItems && (
              <div
                style={{
                  marginTop: '16px',
                  padding: '8px 8px 8px 0',
                  backgroundColor: '#f5f5f5',
                  borderRadius: '4px',
                  fontSize: '14px',
                  color: '#666',
                }}
              >
                Showing first {MAX_ITEMS_TO_RENDER} of {response.length} items.
                {response.length - MAX_ITEMS_TO_RENDER} more items are available.
              </div>
            )}
          </div>
        )}
      </>
    </div>
  );
};

export default React.memo(SearchAnswerContainer);
